import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Check, CreditCard, Star, AlertTriangle, MessageSquare, FileText, Shield, Zap, Building
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Pricing = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const handlePayment = async (tier) => {
    setLoading(tier);
    try {
      // Standardized plan mapping - matches backend STRIPE_PRICE_ALLOWLIST
      const planMap = {
        instant: 'scam_check_basic',      // £4.99 - Instant Scam Check
        advanced: 'scam_check_pro',       // £9.99 - Advanced Risk Analysis
        professional: 'starter',          // £49.00 - Professional Fraud Assessment
        dossier: 'dossier',               // £1,250.00 - Investigation Dossier
        corporate: 'corporate'            // £3,250.00 - Corporate Investigation
      };

      const plan = planMap[tier];
      
      const response = await fetch(`${API_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: plan,
          originUrl: window.location.origin
        })
      });

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Unable to process payment. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1628] pt-24 pb-16">
      <Helmet>
        <title>Pricing | ActionFraudOne</title>
        <meta name="description" content="Scam Check from £4.99. Investigation Dossier £1,250. Professional fraud investigation and scam protection services." />
      </Helmet>

      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-gray-400 text-lg">
            Choose the service that matches your needs. From quick scam verification to comprehensive fraud investigation.
          </p>
        </div>

        {/* Pricing Cards - 5 Tier Structure */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-16">
          
          {/* Tier 1: Instant Scam Check - £4.99 */}
          <Card className="bg-[#0d1d32] border-blue-500/20 hover:border-blue-500/40 transition-all">
            <CardContent className="p-8">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-6 h-6 text-blue-400" />
                <h3 className="text-xl font-bold text-white">Instant Scam Check</h3>
              </div>
              
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-bold text-blue-400">£4.99</span>
                <span className="text-gray-500">one-time</span>
              </div>
              
              <p className="text-gray-400 mb-6 min-h-[60px]">
                Basic automated fraud risk scan with key indicators.
              </p>
              
              <ul className="space-y-3 mb-8">
                {[
                  'Fraud Risk Score (0-100)',
                  'Basic Risk Indicators',
                  'AI Analysis Summary',
                  'Instant Results'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              
              <Button 
                onClick={() => handlePayment('instant')}
                disabled={loading === 'instant'}
                className="w-full bg-[#1e40af] hover:bg-[#1e3a8a] text-white font-bold"
                data-testid="pricing-instant-btn"
              >
                {loading === 'instant' ? 'Processing...' : 'Run Check - £4.99'}
              </Button>
            </CardContent>
          </Card>

          {/* Tier 2: Advanced Risk Analysis - £9.99 */}
          <Card className="bg-[#0d1d32] border-cyan-500/30 hover:border-cyan-500/50 transition-all relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-cyan-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
              <Star size={12} /> RECOMMENDED
            </div>
            <CardContent className="p-8">
              <div className="flex items-center gap-2 mb-4">
                <Zap className="w-6 h-6 text-cyan-400" />
                <h3 className="text-xl font-bold text-white">Advanced Risk Analysis</h3>
              </div>
              
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-bold text-cyan-400">£9.99</span>
                <span className="text-gray-500">one-time</span>
              </div>
              
              <p className="text-gray-400 mb-6 min-h-[60px]">
                Extended fraud intelligence analysis with deeper indicators.
              </p>
              
              <ul className="space-y-3 mb-8">
                {[
                  'Everything in Instant Check',
                  'Deep Fraud Indicators',
                  'Pattern Analysis',
                  'Related Scam Reports',
                  'Recommended Actions',
                  'Priority Support'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-cyan-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              
              <Button 
                onClick={() => handlePayment('advanced')}
                disabled={loading === 'advanced'}
                className="w-full bg-gradient-to-r from-[#1e40af] to-cyan-500 hover:from-[#1e3a8a] hover:to-cyan-400 text-white font-bold"
                data-testid="pricing-advanced-btn"
              >
                {loading === 'advanced' ? 'Processing...' : 'Get Analysis - £9.99'}
              </Button>
            </CardContent>
          </Card>

          {/* Tier 3: Professional Fraud Assessment - £49.00 */}
          <Card className="bg-[#0d1d32] border-purple-500/20 hover:border-purple-500/40 transition-all">
            <CardContent className="p-8">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-6 h-6 text-purple-400" />
                <h3 className="text-xl font-bold text-white">Professional Assessment</h3>
              </div>
              
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-bold text-purple-400">£49.00</span>
                <span className="text-gray-500">one-time</span>
              </div>
              
              <p className="text-gray-400 mb-6 min-h-[60px]">
                Enhanced OSINT review with deeper entity analysis.
              </p>
              
              <ul className="space-y-3 mb-8">
                {[
                  'OSINT Intelligence Review',
                  'Digital Footprint Analysis',
                  'Entity Verification',
                  'Evidence Summary Report',
                  '48-72hr Turnaround'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              
              <Button 
                onClick={() => handlePayment('professional')}
                disabled={loading === 'professional'}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold"
                data-testid="pricing-professional-btn"
              >
                {loading === 'professional' ? 'Processing...' : 'Start Assessment - £49'}
              </Button>
            </CardContent>
          </Card>

          {/* Tier 4: Investigation Dossier - £1,250.00 */}
          <Card className="bg-[#0d1d32] border-orange-500/20 hover:border-orange-500/40 transition-all">
            <CardContent className="p-8">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-6 h-6 text-orange-400" />
                <h3 className="text-xl font-bold text-white">Investigation Dossier</h3>
              </div>
              
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-bold text-orange-400">£1,250</span>
                <span className="text-gray-500">one-time</span>
              </div>
              
              <p className="text-gray-400 mb-6 min-h-[60px]">
                Full manual investigation for legal, police, or bank use.
              </p>
              
              <ul className="space-y-3 mb-8">
                {[
                  'Full Manual Investigation',
                  'Transaction Timeline Reconstruction',
                  'Legal-Ready Evidence Pack',
                  'Professional PDF Dossier',
                  'Expert Consultation Call'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              
              <Button 
                onClick={() => handlePayment('dossier')}
                disabled={loading === 'dossier'}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold"
                data-testid="pricing-dossier-btn"
              >
                {loading === 'dossier' ? 'Processing...' : 'Purchase Dossier - £1,250'}
              </Button>
            </CardContent>
          </Card>

          {/* Tier 5: Corporate Investigation - £3,250.00 */}
          <Card className="bg-[#0d1d32] border-slate-500/20 hover:border-slate-500/40 transition-all">
            <CardContent className="p-8">
              <div className="flex items-center gap-2 mb-4">
                <Building className="w-6 h-6 text-slate-400" />
                <h3 className="text-xl font-bold text-white">Corporate Investigation</h3>
              </div>
              
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-bold text-slate-300">£3,250</span>
                <span className="text-gray-500">one-time</span>
              </div>
              
              <p className="text-gray-400 mb-6 min-h-[60px]">
                Deep corporate due diligence investigation.
              </p>
              
              <ul className="space-y-3 mb-8">
                {[
                  'Corporate Due Diligence',
                  'Director & Shareholder Analysis',
                  'Company Background Intelligence',
                  'Transaction Risk Assessment',
                  'Board-Level Report'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              
              <Button 
                onClick={() => handlePayment('corporate')}
                disabled={loading === 'corporate'}
                className="w-full bg-slate-600 hover:bg-slate-700 text-white font-bold"
                data-testid="pricing-corporate-btn"
              >
                {loading === 'corporate' ? 'Processing...' : 'Begin Investigation - £3,250'}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Trust Signals */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#0d1d32] border border-[#1e40af]/20 rounded-xl p-8">
            <h3 className="text-xl font-bold text-white mb-6 text-center">Why Choose ActionFraudOne?</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <Shield className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
                <h4 className="text-white font-semibold mb-2">Secure Payment</h4>
                <p className="text-gray-500 text-sm">All transactions secured by Stripe</p>
              </div>
              <div className="text-center">
                <FileText className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
                <h4 className="text-white font-semibold mb-2">Professional Reports</h4>
                <p className="text-gray-500 text-sm">Legal-ready documentation</p>
              </div>
              <div className="text-center">
                <AlertTriangle className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
                <h4 className="text-white font-semibold mb-2">Fraud Expertise</h4>
                <p className="text-gray-500 text-sm">Specialized investigation team</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
